import React, { useState, useEffect } from 'react';
import FormCard from '../../../../components/Layouts/Default/FormCard';
import { NextUI } from '@/components/NextUI';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { StarIcon } from '../../../../components/Layouts/Default/StarIcon';
import { db } from '@/lib/firebaseConfig';
import {
  doc,
  updateDoc,
  increment,
  collection,
  addDoc,
  getDoc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { useAppSelector } from '@/hooks/redux';
import { useAppDispatch } from '@/hooks/redux';
import { hideModal } from '@/store/slices/modalSlice';
import { useTranslations } from 'next-intl';

const RateTool = () => {
  const t = useTranslations('modals.rate_tool');
  const modalState = useAppSelector((state) => state.modal);
  const activeModal = modalState.activeModal;
  const modalData = activeModal
    ? modalState.modals[activeModal]?.modalData
    : null;

  const toolId = modalData?.toolId;
  const initialRating = modalData?.initialRating || 5;

  // Form state
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState(initialRating);
  const [hover, setHover] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add a state to track if component is mounted
  const [isMounted, setIsMounted] = useState(false);

  // Add this useEffect to handle mounting state
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Update the existing useEffect
  useEffect(() => {
    if (modalData?.initialRating) {
      setRating(modalData.initialRating);
    }

    // Only access localStorage after component has mounted
    if (isMounted) {
      // Try to get stored name from localStorage
      const storedName = localStorage.getItem('user_name');
      if (storedName) {
        setName(storedName);
      }
    }
  }, [modalData, isMounted]); // Add isMounted dependency

  const dispatch = useAppDispatch();
  const totalStars = 5;

  const handleClick = (index: number) => {
    setRating(index);
  };

  const submitRating = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError(t('name_required_error'));
      return;
    }

    // Add validation for toolId
    if (!toolId) {
      setError(t('tool_info_error'));
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      localStorage.setItem('user_name', name);

      // 1. Get reference to the tool's rating document
      const ratingDocRef = doc(db, 'ratings', toolId);

      // 2. Check if the document exists
      const docSnap = await getDoc(ratingDocRef);

      if (docSnap.exists()) {
        // 3. Update the existing rating document
        await updateDoc(ratingDocRef, {
          totalScore: increment(rating),
          totalRatings: increment(1),
          lastUpdated: serverTimestamp(),
        });
      } else {
        // 4. Create a new rating document if it doesn't exist
        await setDoc(ratingDocRef, {
          tool: toolId,
          totalScore: rating,
          totalRatings: 1,
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp(),
        });
      }

      // 5. Add a new review to the subcollection
      const reviewsCollectionRef = collection(db, 'ratings', toolId, 'reviews');
      await addDoc(reviewsCollectionRef, {
        name: name,
        message: message,
        rating: rating,
        createdAt: serverTimestamp(),
      });

      // Save rating to localStorage
      const userRatings = JSON.parse(
        localStorage.getItem('user_ratings') || '{}'
      );
      userRatings[toolId] = {
        rating: rating,
        name: name,
        message: message,
        date: new Date().toISOString(),
      };
      localStorage.setItem('user_ratings', JSON.stringify(userRatings));

      // Call the refresh callback if it exists
      if (modalData?.onRatingSubmitted) {
        modalData.onRatingSubmitted();
      }

      // Set success message
      setSuccess('rated');

      // Close modal after delay
      setTimeout(() => {
        dispatch(hideModal());
      }, 3000); // Extended to 3 seconds to allow user to read the message
    } catch (err) {
      console.error('Error details:', err); // Log the full error
      setError(t('submit_error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <NextUI.Modal
      className="max-w-[500px]"
      classNames={{ content: 'py-8 !px-4 md:!px-8 lg:!px-12' }}
      defaultOpen={true}
      name={'RateTool'}
      backdrop="blur"
      closeButton={
        <FontAwesomeIcon
          icon={['far', 'circle-xmark']}
          className="text-primary-blue"
        />
      }
    >
      {success ? (
        <div className="flex flex-col items-center justify-center py-6">
          <div className="text-green-500 text-5xl mb-4">
            <FontAwesomeIcon icon={['fas', 'circle-check']} />
          </div>
          <h2 className="h5 font-semibold text-center text-dark mb-2">
            {t('thank_you')}
          </h2>
          <p
            className="text-center text-gray-600"
            dangerouslySetInnerHTML={{
              __html: t('success_message', { rating }),
            }}
          />
          <div className="flex gap-1 mt-4 mb-2">
            {[...Array(totalStars)].map((_, index) => (
              <StarIcon key={index} filled={index < rating} />
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-4">{t('closing_message')}</p>
        </div>
      ) : (
        <>
          <h2 className="h5 font-semibold text-center text-dark">
            {t('title')}
          </h2>

          <p className="mt-6 body-normal text-center text-gray-dark">
            {t('subtitle')}
          </p>

          {error && (
            <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-md text-center">
              {error}
            </div>
          )}

          <FormCard
            actionText={
              isSubmitting ? t('submitting_button') : t('submit_button')
            }
            className="mt-8"
            classNames={{
              actionButton: 'max-w-[160px]',
              actionWrapper: 'mt-8 pb-6',
              formBody: 'p-0 gap-5',
            }}
            onSubmit={submitRating}
            disabled={isSubmitting}
          >
            <NextUI.Input
              type="name"
              formNoValidate
              variant="outline"
              radius="sm"
              placeholder={t('name_placeholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              isRequired
            />
            <NextUI.TextArea
              placeholder={t('review_placeholder')}
              variant="outline"
              radius="sm"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              classNames={{
                input: 'min-h-[102px]',
              }}
            />
            <div className="justify-center flex">
              <div className="flex gap-2 bg-primary-extra-light-blue2 p-4 rounded-extra-lg">
                {[...Array(totalStars)].map((_, index) => {
                  const ratingValue = index + 1;
                  return (
                    <span
                      key={index}
                      className="cursor-pointer"
                      onMouseEnter={() => setHover(ratingValue)}
                      onMouseLeave={() => setHover(0)}
                      onClick={() => handleClick(ratingValue)}
                    >
                      <StarIcon filled={ratingValue <= (hover || rating)} />
                    </span>
                  );
                })}
              </div>
            </div>
          </FormCard>
        </>
      )}
    </NextUI.Modal>
  );
};

export default RateTool;
